// row 107 DocPane (`docpane`, DS DC-83, candidate C-6/C-15): the document preview well — --bg-deep, --r-tile — under a
// header with the document's name (*Original resume*), a page stepper (back / forward IconButtons, each named by
// the page it opens, around "n of N", announced politely; absent at the first and last page, and when the pressed one
// leaves, focus moves to the one that stays) and a zoom IconButton that steps through the zoom levels. The well is a focusable region named
// by the heading, so a zoomed page scrolls from the keyboard. loading: a skeleton page. error: the clear error for an
// invalid, password-protected or unreadable file as a flag Banner (never a partial page). With `fields`, the pane sits
// beside them (5 : 7) and stacks above them at ≤ 1180. `pages` is a count (the children draw each page) or the pages
// themselves (B3-4): an array whose length is the count, a string drawn as the page's text with its line breaks kept.

import { useRef, useState, type ReactNode } from "react";
import { Banner, IconButton, Skeleton } from "../core";
import { cx } from "../lib/cx";
import { useIsoLayoutEffect } from "../lib/useIsoLayoutEffect";
import { useSvgId } from "../lib/useSvgId";
import { vars } from "../lib/vars";
import { DOCUMENT_LABELS, G7_TEMPLATES } from "./labels";
import type { Hs } from "../lib/hs";

export type DocError = "invalid" | "passwordProtected" | "unreadable";

const ERROR_WORD: Readonly<Record<DocError, string>> = {
  invalid: DOCUMENT_LABELS.invalid,
  passwordProtected: DOCUMENT_LABELS.passwordProtected,
  unreadable: DOCUMENT_LABELS.unreadable,
};

/** The zoom steps, as percentages. */
const ZOOM = [100, 125, 150] as const;

export interface DocPaneProps {
  /** The page shown: content, or a function of the page number (1-based). */
  children?: ReactNode | ((page: number) => ReactNode);
  /** The heading (default *Original resume*). */
  title?: ReactNode | undefined;
  /** A meta beside the heading (a version tag). */
  sub?: ReactNode | undefined;
  /** The page count, or the pages (a string is the page's text; children are then not used). */
  pages?: number | readonly ReactNode[] | undefined;
  page?: number | undefined;
  defaultPage?: number | undefined;
  onPage?: ((page: number) => void) | undefined;
  loading?: boolean | undefined;
  error?: DocError | undefined;
  /** The fields beside the pane (the reviewed values). */
  fields?: ReactNode | undefined;
  can?: boolean | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

export function DocPane({
  children, title = DOCUMENT_LABELS.originalResume, sub, pages = 1, page, defaultPage = 1, onPage, loading, error, fields, can, hs, className,
}: DocPaneProps) {
  const base = useSvgId();
  const [own, setOwn] = useState(defaultPage);
  const [zoom, setZoom] = useState(0);
  const backRef = useRef<HTMLButtonElement>(null);
  const fwdRef = useRef<HTMLButtonElement>(null);
  // The press in flight: its direction and the page it asked for. It is cleared after the next commit whatever that
  // commit shows, so a press the host refused never steers focus on a later, outside change of page.
  const pressed = useRef<{ dir: "back" | "fwd"; to: number } | null>(null);
  const list = typeof pages === "number" ? undefined : pages;
  const total = Math.max(1, typeof pages === "number" ? pages : pages.length);
  const current = Math.min(Math.max(1, page ?? own), total);
  // The button just pressed is left out at the first or last page: keep focus in the stepper, on the one that stays.
  useIsoLayoutEffect(() => {
    const press = pressed.current;
    pressed.current = null;
    if (!press || press.to !== current) return;
    const lost = !document.activeElement || document.activeElement === document.body;
    if (!lost) return;
    if (press.dir === "fwd" && !fwdRef.current) backRef.current?.focus();
    else if (press.dir === "back" && !backRef.current) fwdRef.current?.focus();
  });
  if (can === false) return null;
  const go = (next: number) => {
    if (next < 1 || next > total) return;
    pressed.current = { dir: next < current ? "back" : "fwd", to: next };
    if (page === undefined) setOwn(next);
    onPage?.(next);
  };
  const headingId = `${base}-h`;
  const nextZoom = ZOOM[(zoom + 1) % ZOOM.length] ?? ZOOM[0];
  const tools = !loading && !error;
  let well: ReactNode;
  if (error) {
    well = <Banner tone="flag" live>{ERROR_WORD[error]} · {DOCUMENT_LABELS.pdfAndDocx}</Banner>;
  } else if (loading) {
    well = <div className="mc-doc-well" aria-busy="true"><span className="mc-doc-page mc-doc-skel"><Skeleton lines={9} height={10} /></span></div>;
  } else {
    well = (
      <div className="mc-doc-well" role="region" aria-labelledby={headingId} tabIndex={0}>
        <div className="mc-doc-page" style={vars({ "--mc-zoom": (ZOOM[zoom] ?? 100) / 100 })}>
          {list ? (typeof list[current - 1] === "string" ? <div className="mc-doc-text">{list[current - 1]}</div> : (list[current - 1] ?? null))
            : typeof children === "function" ? children(current) : children}
        </div>
      </div>
    );
  }
  const pane = (
    <div className={cx("mc-docpane", !fields && className)}>
      <div className="mc-doc-h">
        <h3 id={headingId} className="mc-doc-t">{title}</h3>
        {sub !== undefined ? <span className="mc-doc-s">{sub}</span> : null}
        {tools ? (
          <div className="mc-doc-tools">
            <div className="mc-doc-step" role="group" aria-label={DOCUMENT_LABELS.pageStepper}>
              <IconButton ref={backRef} sm icon="back" label={G7_TEMPLATES.pageOf({ n: current - 1, total })} onClick={() => go(current - 1)} can={current > 1} hs={hs} />
              <span className="mc-doc-n" aria-live="polite">{G7_TEMPLATES.pageOf({ n: current, total })}</span>
              <IconButton ref={fwdRef} sm icon="fwd" label={G7_TEMPLATES.pageOf({ n: current + 1, total })} onClick={() => go(current + 1)} can={current < total} hs={hs} />
            </div>
            <div className="mc-doc-zoom" role="group" aria-label={DOCUMENT_LABELS.zoom}>
              <span className="mc-doc-n">{`${ZOOM[zoom] ?? 100}%`}</span>
              <IconButton sm icon="search" label={`${nextZoom}%`} onClick={() => setZoom((zoom + 1) % ZOOM.length)} hs={hs} />
            </div>
          </div>
        ) : null}
      </div>
      {well}
    </div>
  );
  if (!fields) return pane;
  return (
    <div className={cx("mc-docsplit", className)}>
      {pane}
      <div className="mc-doc-fields">{fields}</div>
    </div>
  );
}
